import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

type Row = {
  id: string; name: string;
  min_hours_before: number; max_hours_before: number | null;
  refund_pct: number; display_order: number; active: boolean;
};

export default function AdminRefundPolicies() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("refund_policies" as any)
      .select("*").order("display_order");
    if (error) toast.error(error.message);
    setRows((data ?? []) as unknown as Row[]);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async (r: Row) => {
    const { error } = await supabase.from("refund_policies" as any).update({
      name: r.name, min_hours_before: r.min_hours_before,
      max_hours_before: r.max_hours_before, refund_pct: r.refund_pct,
      display_order: r.display_order, active: r.active,
    }).eq("id", r.id);
    if (error) return toast.error(error.message);
    toast.success("Saved");
  };

  const add = async () => {
    const { error } = await supabase.from("refund_policies" as any).insert({
      name: "New tier", min_hours_before: 0, refund_pct: 0,
      display_order: rows.length + 1, active: true,
    });
    if (error) return toast.error(error.message);
    load();
  };

  const del = async (id: string) => {
    if (!confirm("Delete this tier?")) return;
    await supabase.from("refund_policies" as any).delete().eq("id", id);
    load();
  };

  return (
    <div className="pt-16 min-h-screen">
      <div className="p-6 lg:p-10 max-w-5xl mx-auto">
        <Link to="/admin" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 text-sm">
          <ArrowLeft className="w-4 h-4" /> Back to admin
        </Link>
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-display tracking-widest">REFUND POLICIES</h1>
          <Button onClick={add} className="gap-2"><Plus className="w-4 h-4" /> Add tier</Button>
        </div>
        <p className="text-muted-foreground text-sm mb-6 max-w-2xl">
          Tiered by hours-before-start. The booking falls into the tier where
          <code className="mx-1">hoursBefore</code> is &ge; <code>min</code> and &lt; <code>max</code> (max blank = unlimited).
          Lower display order is evaluated first.
        </p>
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : (
          <div className="bg-surface border border-border">
            <div className="grid grid-cols-[1.5fr_repeat(4,90px)_60px_50px_auto] gap-3 p-3 text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <span>Name</span><span>Min h</span><span>Max h</span><span>Refund %</span><span>Order</span><span>Active</span><span></span><span></span>
            </div>
            {rows.map((r) => (
              <div key={r.id} className="grid grid-cols-[1.5fr_repeat(4,90px)_60px_50px_auto] gap-3 p-3 items-center border-b border-border last:border-0">
                <Input value={r.name} onChange={(e) => setRows(rows.map(x => x.id===r.id ? {...x, name:e.target.value} : x))} />
                <Input type="number" value={r.min_hours_before} onChange={(e) => setRows(rows.map(x => x.id===r.id ? {...x, min_hours_before:Number(e.target.value)} : x))} />
                <Input type="number" value={r.max_hours_before ?? ""} placeholder="∞" onChange={(e) => setRows(rows.map(x => x.id===r.id ? {...x, max_hours_before: e.target.value===""?null:Number(e.target.value)} : x))} />
                <Input type="number" step="0.01" value={r.refund_pct} onChange={(e) => setRows(rows.map(x => x.id===r.id ? {...x, refund_pct:Number(e.target.value)} : x))} />
                <Input type="number" value={r.display_order} onChange={(e) => setRows(rows.map(x => x.id===r.id ? {...x, display_order:Number(e.target.value)} : x))} />
                <Switch checked={r.active} onCheckedChange={(v) => setRows(rows.map(x => x.id===r.id ? {...x, active:v} : x))} />
                <Button size="sm" variant="outline" onClick={() => save(r)}>Save</Button>
                <Button size="sm" variant="ghost" onClick={() => del(r.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}